import dynamic from "next/dynamic";
import BannerMaterial from "@/components/showcase/bannerMaterial";
import BannerSlider from "@/components/showcase/bannerSlider";
import Cards from "@/components/content/cards";
import ImageAnimations from "@/components/showcase/imageAnimations";
import OnlineBakhish from "@/components/showcase/onlineBakhish";
import PentaBanners from "@/components/showcase/pentaBanners";

const HomeSwiper = dynamic(() => import("@/components/showcase/swiper"), {
  ssr: false,
  loading: () => (
    <div className="w-full lg:h-[78vh] min-[300px]:h-[auto] max-[800px]:h-[auto] bg-black" />
  ),
});
import TurkiLar from "@/components/showcase/turkiLar";
import { isContentAllowedForAgeRange } from "@/helpers/ageRange.utils";
import { store } from "@/redux/store";
import {
  orderShowcaseItemsByContentKeyPriority,
  shouldPreserveShowcaseOrder,
} from "@/components/showcase/showcaseOrder.utils";
import { getShowcaseNextPlayGuide } from "@/helpers/nextPlayGuideHelper";
import {
  parseMovieSliderTargetURI,
  parseShowcaseTargetURI,
} from "@/components/showcase/showcaseTarget.utils";

export { parseShowcaseTargetURI } from "@/components/showcase/showcaseTarget.utils";

const MAINSlider = "mainSlider";
const TURKYLAR = "infinitySlider";
const MOFTABAKH = "freeMovies";
const ONLINEBAKHISH = "onlineCinema";
const MOVIESlider = "moiveSlider";
const MOVIESliderAlt = "movieSlider";
const TOPABAKH = "sportList";
const PENTABanner = "pentaBanner";
const Material = "materialUSilder";
const MaterialAlt = "materialUSlider";
const Nostalgia = "nostalgia";
const ARTISTSlider = "artistSlider";
const LARGEMOVIE = "largeMovieSlider";

// Private/personalized showcase types
const CONTINUE_WATCH = "continueWatch";
const YOU_MAY_LIKE = "youMayLike";

export const getShowcaseImageURL = (imageName, params = {}) => {
  const base = `https://fcdn.manabakh.ir/images/${encodeURIComponent(
    imageName
  )}`;
  const query = [];
  if (params.format) query.push(`f=${params.format}`);
  if (params.quality) query.push(`q=${params.quality}`);
  if (params.width) query.push(`w=${params.width}`);
  if (params.auto) query.push("auto=1");
  return query.length ? `${base}?${query.join("&")}` : base;
};

/** Dev-only: format a timestamp as Persian relative time (e.g. "۲ ساعت پیش") */
const getRelativeTime = (ts) => {
  var diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 0) return "آینده";
  if (diff < 60) return diff + " ثانیه پیش";
  var mins = Math.floor(diff / 60);
  if (mins < 60) return mins + " دقیقه پیش";
  var hours = Math.floor(mins / 60);
  if (hours < 24) return hours + " ساعت پیش";
  var days = Math.floor(hours / 24);
  if (days < 30) return days + " روز پیش";
  var months = Math.floor(days / 30);
  return months + " ماه پیش";
};

export const showCaseComponentSwitch = (
  {
    type,
    kind,
    items = [],
    title,
    description,
    backgroundImage,
    staticItems = [],
    actionLink,
    hasMore,
    categoryKeys,
    genreKeys,
    contentKeys,
  },
  targetKind,
  index
) => {
  const authState = store.getState().Auth;
  const ageRange = authState.ageRange || 120;
  const isLoggedIn = Boolean(
    authState.isLogin || authState.profile?.isLogin || authState.userToken
  );

  if ([CONTINUE_WATCH, YOU_MAY_LIKE].includes(type) && !isLoggedIn) {
    return null;
  }

  // Private showcase types send raw content objects (not wrapped in { content: ... })
  const isPrivateShowcase = [
    CONTINUE_WATCH,
    YOU_MAY_LIKE,
  ].includes(type);

  const _items = isPrivateShowcase
    ? items.filter((x) => isContentAllowedForAgeRange(x.ageRange, ageRange))
    : items.filter((x) =>
        isContentAllowedForAgeRange(x.content?.ageRange, ageRange, {
          allowEmpty: false,
        })
      );
  const preserveOrder = shouldPreserveShowcaseOrder(description);
  const displayItems = preserveOrder
    ? orderShowcaseItemsByContentKeyPriority(_items, contentKeys)
    : _items;
  const artistItems = preserveOrder
    ? orderShowcaseItemsByContentKeyPriority(items, contentKeys)
    : items;

  // Normalize private showcase items to { content: ... } format for Cards
  const normalizedItems = isPrivateShowcase
    ? displayItems.map((content) => ({ content, sliderType: "portrait" }))
    : displayItems;

  const nextPlayGuide = getShowcaseNextPlayGuide({
    categoryKeys,
    genreKeys,
    actionLink,
    title,
  });

  if (
    _items.length < 1 &&
    staticItems.length < 1 &&
    type !== MOFTABAKH &&
    type !== ARTISTSlider
  ) {
    return null;
  }

  if (items?.length > 0 || staticItems?.length > 0) {
    switch (type) {
      case MAINSlider:
        return (
          <HomeSwiper
            key={`showcase-${index}`}
            items={displayItems}
            preserveOrder={preserveOrder}
            nextPlayGuide={nextPlayGuide}
          />
        );
      case TURKYLAR:
        return (
          <div
            key={`showcase-${index}`}
            className="lg:max-w-[70%] mx-auto w-full mb-10 lg:-mt-20 relative z-10"
          >
            <TurkiLar
              items={displayItems}
              categoryKey={categoryKeys?.[0] || "exclusive-dubbed"}
              preserveOrder={preserveOrder}
              nextPlayGuide={nextPlayGuide}
            />
          </div>
        );
      case MOFTABAKH:
        return (
          <div
            key={`showcase-${index}`}
            className="manabakh-shadow h-[200px] sm:h-[280px] md:h-[330px] lg:h-[380px] pb-[220px] sm:pb-[220px] md:pb-[300px] lg:pb-[380px] w-full flex justify-center overflow-hidden relative mb-10"
          >
            <ImageAnimations />
          </div>
        );
      case ONLINEBAKHISH:
        return (
          <div
            key={`showcase-${index}`}
            className="mt-10 md:mt-24 md:container mx-auto pb-10 w-full z-10"
          >
            <OnlineBakhish items={displayItems} />
          </div>
        );
      case TOPABAKH:
        return (
          <div
            key={`showcase-${index}`}
            className="topobakh-background relative mb-24"
          >
            <Cards
              items={displayItems}
              isSwiper={true}
              categoryName={title}
              type="sport"
              showTitle={true}
              hasExpand={parseShowcaseTargetURI(actionLink) && hasMore}
              actionLink={parseShowcaseTargetURI(actionLink, targetKind)}
              hasShadow={true}
              noShadow={true}
              isLive={true}
              preserveOrder={preserveOrder}
              nextPlayGuide={nextPlayGuide}
            />
          </div>
        );
      case MOVIESlider:
        return (
          <div key={`showcase-${index}`} className="mb-24">
            <Cards
              items={displayItems}
              isSwiper={true}
              categoryName={title}
              actionLink={parseMovieSliderTargetURI(actionLink, targetKind)}
              showTitle={true}
              type={kind}
              hasExpand={parseShowcaseTargetURI(actionLink) && hasMore}
              isArtist={false}
              preserveOrder={preserveOrder}
              nextPlayGuide={nextPlayGuide}
            />
          </div>
        );
      case MOVIESliderAlt:
        return (
          <div key={`showcase-${index}`} className="mb-24">
            <Cards
              items={displayItems}
              isSwiper={true}
              categoryName={title}
              showTitle={true}
              type={kind}
              actionLink={parseMovieSliderTargetURI(actionLink, targetKind)}
              isArtist={false}
              hasExpand={parseShowcaseTargetURI(actionLink) && hasMore}
              preserveOrder={preserveOrder}
              nextPlayGuide={nextPlayGuide}
            />
          </div>
        );
      case ARTISTSlider:
        return (
          <div key={`showcase-${index}`} className="mb-24 mt-16">
            <Cards
              items={artistItems}
              isSwiper={true}
              categoryName={title}
              type={"artist"}
              showTitle={true}
              isArtist={true}
              preserveOrder={preserveOrder}
            />
          </div>
        );
      case Nostalgia:
        return (
          <div key={`showcase-${index}`} className="my-5">
            <BannerSlider items={staticItems} />
          </div>
        );
      case Material:
        return (
          <div
            key={`showcase-${index}`}
            className="flex w-screen items-center justify-center mt-24 mb-12 md:h-[500px]"
          >
            <BannerMaterial items={staticItems} />
          </div>
        );
      case MaterialAlt:
        return (
          <div
            key={`showcase-${index}`}
            className="flex w-screen items-center justify-center mt-24 mb-12 md:h-[500px]"
          >
            <BannerMaterial items={staticItems} />
          </div>
        );
      case PENTABanner:
        return (
          <div key={`showcase-${index}`} className="my-24 lg:px-10">
            <PentaBanners
              bannerTitle={title}
              bannerDescription={description}
              bannerBackground={getShowcaseImageURL(backgroundImage, {
                quality: 90,
                width: 1920,
              })}
              bannerItems={displayItems}
              preserveOrder={preserveOrder}
              nextPlayGuide={nextPlayGuide}
            />
          </div>
        );
      case LARGEMOVIE:
        return (
          <div key={`showcase-${index}`} className="mb-24">
            <Cards
              items={displayItems}
              isSwiper={true}
              categoryName={title}
              type={"sport"}
              showTitle={true}
              hasExpand={false}
              preserveOrder={preserveOrder}
              nextPlayGuide={nextPlayGuide}
            />
          </div>
        );
      // Private/personalized showcase types
      case CONTINUE_WATCH:
        return (
          <div key={`showcase-${index}`} className="mb-24">
            <Cards
              items={normalizedItems}
              isSwiper={true}
              categoryName={title}
              type={"movie"}
              showTitle={true}
              hasExpand={false}
              isContinueWatch={true}
            />
          </div>
        );
      case YOU_MAY_LIKE:
        return (
          <div key={`showcase-${index}`} className="mb-24">
            <Cards
              items={normalizedItems}
              isSwiper={true}
              categoryName={title}
              type={"movie"}
              showTitle={true}
              hasExpand={false}
            />
          </div>
        );
      default:
        return null;
    }
  } else {
    return null;
  }
};
